<template>
  <el-dialog
    :model-value="modelValue"
    title="录屏回放"
    width="92vw"
    :align-center="true"
    :close-on-click-modal="false"
    class="record-dialog"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div ref="container" class="player-box"></div>
  </el-dialog>
</template>

<script setup lang="ts">
import { nextTick, ref, watch, onUnmounted } from "vue";
import { Replayer } from "rrweb";
import "rrweb/dist/style.css";
import { ungzip } from "pako";
import { Base64 } from "js-base64";
import { ElMessage } from "element-plus";

const props = defineProps<{ modelValue: boolean; events: string }>();
const emit = defineEmits<{ "update:modelValue": [value: boolean] }>();

const container = ref<HTMLElement>();
let replayerInstance: any = null;

function unpack(data: string): any[] {
  const binary = Base64.atob(data);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  const restored = new TextDecoder().decode(ungzip(bytes));
  return JSON.parse(Base64.decode(restored));
}

function destroyReplayer() {
  if (replayerInstance) {
    try {
      replayerInstance.destroy();
    } catch (e) {
      // ignore
    }
    replayerInstance = null;
  }
}

watch(
  () => props.modelValue,
  async (visible) => {
    if (!visible) {
      destroyReplayer();
      return;
    }
    await nextTick();
    try {
      const events = unpack(props.events);

      const fullSnapshot = events.find((e) => e.type === 2);

      if (!container.value) return;

      // 先销毁旧的 Replayer
      destroyReplayer();
      container.value.innerHTML = "";

      if (!fullSnapshot) {
        ElMessage.error("录屏数据缺少全量快照，无法播放");
        return;
      }

      // 确保事件数组以 FullSnapshot 开始（如果前面有 Meta 等事件，截取从 FullSnapshot 开始）
      let playEvents = events;
      const firstFsIndex = events.findIndex((e) => e.type === 2);
      if (firstFsIndex > 0) {
        // 前面有非 FullSnapshot 事件，截取
        playEvents = events.slice(firstFsIndex);
      }

      // 获取原始页面尺寸
      const fsWidth = fullSnapshot?.data?.width || 1920;
      const fsHeight = fullSnapshot?.data?.height || 1080;

      // 计算缩放比例，按宽度适配
      const containerWidth = container.value.clientWidth;
      const scale = containerWidth / fsWidth;
      const scaledHeight = Math.round(fsHeight * scale);

      replayerInstance = new Replayer(playEvents, {
        root: container.value,
        speed: 1,
        skipInactive: false,
      });

      replayerInstance.play();

      // 延迟设置 iframe 样式
      setTimeout(() => {
        const iframe = container.value?.querySelector("iframe");
        if (iframe) {
          const iframeEl = iframe as HTMLElement;
          iframeEl.style.cssText = `width: ${fsWidth}px !important; height: ${fsHeight}px !important; border: none; display: block;`;

          const wrapper = iframe.parentElement;
          if (wrapper) {
            wrapper.style.cssText = `width: ${fsWidth}px !important; height: ${fsHeight}px !important; position: relative !important; left: 0 !important; top: 0 !important; transform: scale(${scale}) !important; transform-origin: top left !important; float: none !important;`;
          }

          const replayerWrapper = wrapper?.parentElement;
          if (replayerWrapper) {
            replayerWrapper.style.cssText = `width: ${containerWidth}px !important; height: ${scaledHeight}px !important; position: relative !important; overflow: hidden !important;`;
          }
        }
      }, 500);
    } catch (err) {
      ElMessage.error("录屏数据解析失败");
      emit("update:modelValue", false);
    }
  },
);

onUnmounted(() => {
  destroyReplayer();
});
</script>

<style scoped>
.record-dialog :deep(.el-overlay-dialog) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.record-dialog :deep(.el-dialog) {
  margin-top: 0 !important;
  max-height: 92vh;
}

.record-dialog :deep(.el-dialog__body) {
  padding: 0;
  overflow: hidden;
}

.player-box {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(92vh - 55px);
  background: #fff;
  border: 1px solid #ddd;
  position: relative;
  overflow: hidden;
  padding: 0;
  margin: 0;
}

.player-box :deep(.replayer-wrapper) {
  margin: 0 !important;
  padding: 0 !important;
  max-width: 100%;
  max-height: 100%;
}

.player-box :deep(iframe) {
  border: none !important;
  margin: 0 !important;
  padding: 0 !important;
}
</style>
